import i18n from "i18next";

import { getCountryRegionNames, getCountryRegions, updateUserSetting } from "services";

import { getLocalKlimVar, setLocalKlimVar } from "utils";

import { COUNTRY_CODE, LANGUAGE_CODE, LOCALE_DICT } from "consts";

import { GroupedRegion, User, hasGroupedRegions } from "models";

export { type TFunction } from "i18next";

export type RegionsData =
  | {
      isGrouped: false;
      regions: string[];
    }
  | {
      isGrouped: true;
      groupedRegions: GroupedRegion[];
    };

export const getLanguageFromLocalStorage = (forDates?: { forDates?: boolean }) => {
  const lang: LANGUAGE_CODE = getLocalKlimVar("language") || LANGUAGE_CODE.GERMAN;

  if (forDates && lang === LANGUAGE_CODE.ZULU) {
    return LANGUAGE_CODE.ENGLISH;
  }

  return lang;
};

export const getLocalStorageCountry = () => getLocalKlimVar("country");
export const setLocalStorageCountry = (value: string) => setLocalKlimVar("country", value);

export const changeLanguage = (language: LANGUAGE_CODE, user: User | null) =>
  i18n
    .changeLanguage(language)
    .then(() => {
      setLocalKlimVar("language", language);
      document.documentElement.setAttribute("lang", language.toLowerCase());

      if (user) {
        return updateUserSetting({
          item: "language",
          value: language,
        });
      }
    })
    .catch((error) => {
      // eslint-disable-next-line
      console.error(`Changing the language to ${language} has failed`, { error });
    });

export const setLanguageInLocalStorage = () => {
  const languageFromLocalStorage: LANGUAGE_CODE = getLanguageFromLocalStorage() as LANGUAGE_CODE;

  if (i18n.language !== languageFromLocalStorage) {
    return changeLanguage(languageFromLocalStorage || LANGUAGE_CODE.GERMAN, null);
  }
  return Promise.resolve();
};

export const localNumberFormat = () => {
  const language = getLanguageFromLocalStorage();
  const locale = LOCALE_DICT[language] ? LOCALE_DICT[language] : LOCALE_DICT.de;
  return locale;
};

export const getRegionsByCountry = async ({ signal, countryCode }: { signal: AbortSignal; countryCode: COUNTRY_CODE }): Promise<RegionsData> => {
  const response = await getCountryRegionNames({ signal, countryCode });

  if (hasGroupedRegions(response)) {
    // Transform object { "England": [...] } into array [{ country: "England", regions: [...] }]
    const sortedGroupedRegions: GroupedRegion[] = Object.entries(response.groupedRegions)
      .map(([country, regions]) => ({
        country,
        regions: [...regions].sort((a: string, b: string) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.country.localeCompare(b.country));

    return {
      isGrouped: true,
      groupedRegions: sortedGroupedRegions,
    };
  }

  // Sort flat regions alphabetically other than GB
  const sortedRegions = response.regions ? [...response.regions].sort((a, b) => a.localeCompare(b)) : [];

  return {
    isGrouped: false,
    regions: sortedRegions,
  };
};

export const getShapefileRegions = async ({ signal }: { signal: AbortSignal }) => {
  const response = await getCountryRegions({ signal, countryCode: COUNTRY_CODE.GERMANY });
  const germanRegions = response?.regions;
  // edge case for shape files: not seperated by country regions for Austria, Switzerland and Poland
  const shapeFileRegions = { ...germanRegions, AUT: "Österreich", CH: "Schweiz", PL: "Polen" };
  return shapeFileRegions;
};

const LANGUAGE_CODE_MAP = {
  [LANGUAGE_CODE.GERMAN]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.SWISS]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.AUSTRIAN]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.POLISH]: LANGUAGE_CODE.POLISH,
  [LANGUAGE_CODE.CZECH]: LANGUAGE_CODE.CZECH,
  [LANGUAGE_CODE.SLOVAK]: LANGUAGE_CODE.SLOVAK,
  [LANGUAGE_CODE.ENGLISH_GB]: LANGUAGE_CODE.ENGLISH_GB,
  [LANGUAGE_CODE.ENGLISH_US]: LANGUAGE_CODE.ENGLISH_US,
  [LANGUAGE_CODE.ENGLISH]: LANGUAGE_CODE.ENGLISH,
  [LANGUAGE_CODE.ZULU]: LANGUAGE_CODE.ENGLISH,
} as const satisfies Record<LANGUAGE_CODE, LANGUAGE_CODE>;

export const getLanguageCode = (langCode: LANGUAGE_CODE) => LANGUAGE_CODE_MAP[langCode] ?? LANGUAGE_CODE.GERMAN;

export const getLanguageCodeFromCountryCode = (countryCode: string) => {
  switch (countryCode.toUpperCase()) {
    case COUNTRY_CODE.GERMANY:
    case COUNTRY_CODE.AUSTRIA:
    case COUNTRY_CODE.SWITZERLAND:
      return LANGUAGE_CODE.GERMAN;
    case COUNTRY_CODE.POLAND:
      return LANGUAGE_CODE.POLISH;
    case COUNTRY_CODE.CZECHIA:
      return LANGUAGE_CODE.CZECH;
    case COUNTRY_CODE.SLOVAKIA:
      return LANGUAGE_CODE.SLOVAK;
    case COUNTRY_CODE.UNITED_KINGDOM:
      return LANGUAGE_CODE.ENGLISH_GB;
    case COUNTRY_CODE.USA:
      return LANGUAGE_CODE.ENGLISH_US;
    default:
      return LANGUAGE_CODE.GERMAN;
  }
};
